import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth";
import { api, coverUri } from "../lib/api";
import { usePlaylists, usePlaylistMutations, shareUrl } from "../lib/playlists";
import { usePlayer, songsQueue } from "../lib/player";
import { TrackList } from "../components/TrackList";
import { ShareSheet } from "../components/ShareSheet";
import { Button, EmptyState, Spinner } from "../components/ui";
import { Icon } from "../components/icons";

function SongLine({ s, n, actions }) {
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10 }} data-testid={`playlist-song-${s.id}`}>
      <span style={{ width: 20, color: "var(--muted)", fontSize: 12 }}>{n}</span>
      <img className="cover" src={coverUri(s.cover_url)} alt="" style={{ width: 40, height: 40, borderRadius: 6 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
        <div className="t-hint">{s.artist}</div>
      </div>
      {actions}
    </div>
  );
}

function useCatalog() {
  const q = useQuery({ queryKey: ["songs", "", ""], queryFn: () => api("/songs?q=&status=") });
  const byId = useMemo(() => Object.fromEntries((q.data || []).map((s) => [s.id, s])), [q.data]);
  return { q, byId, all: q.data || [] };
}


export function PlaylistDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const player = usePlayer();
  const qc = useQueryClient();
  const { q, byId, all } = useCatalog();
  const listsQ = usePlaylists(user?.id);
  const { update: upd, remove } = usePlaylistMutations();
  const [adding, setAdding] = useState(false);
  const [sharing, setSharing] = useState(false);
  const pl = (listsQ.data || []).find((p) => p.id === id);
  const songs = useMemo(() => (pl ? pl.song_ids.map((sid) => byId[sid]).filter(Boolean) : []), [pl, byId]);
  const key = `playlist:${id}`;
  // Changes to this list are applied to the player immediately when it is playing it.
  const { syncQueue } = player;
  useEffect(() => { if (songs.length) syncQueue(key, songsQueue(songs)); }, [songs, key, syncQueue]);
  if (listsQ.isLoading) return <Spinner />;
  if (!pl) return <div className="page"><EmptyState icon="list-music" text="Lista não encontrada." testid="playlist-not-found" /></div>;

  // Optimistic: the new order is shown (and played) at once, then saved to the account.
  const update = (patch) => {
    qc.setQueryData(["my-playlists", user?.id], (old) => (old || []).map((p) => (p.id === pl.id ? { ...p, ...patch } : p)));
    upd.mutate({ id: pl.id, ...patch }, { onError: (e) => { window.alert(e.message); listsQ.refetch(); } });
  };
  const del = () => {
    if (!window.confirm(`Eliminar a lista «${pl.name}»? As músicas do repertório não são afectadas.`)) return;
    remove.mutate(pl.id, { onSuccess: () => navigate("/listas"), onError: (e) => window.alert(e.message) });
  };

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button className="icon-btn" aria-label="Voltar" onClick={() => navigate("/listas")}><Icon name="chevron-back" size={22} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-kicker">LISTA PESSOAL</div>
          <h1 className="t-h1" style={{ fontSize: 26 }} data-testid="playlist-title">{pl.name}</h1>
        </div>
        <Button className="auto sm ghost" icon="pencil" onClick={() => { const n = window.prompt("Novo nome:", pl.name); if (n?.trim()) update({ name: n.trim() }); }} data-testid="playlist-rename">MUDAR NOME</Button>
        <Button className="auto sm ghost" icon="share" onClick={() => setSharing(true)} disabled={!pl.song_ids.length} data-testid="playlist-share">PARTILHAR</Button>
        <Button className="auto sm ghost" icon="trash" onClick={del} data-testid="playlist-delete">ELIMINAR</Button>
      </div>
      <div className="page ulc-fade" style={{ maxWidth: 820 }}>
        {q.isLoading ? <Spinner /> : (
          <>
            <div style={{ marginBottom: 16 }}>
              <TrackList songs={songs} ctx={{ key, label: pl.name, path: `/listas/${pl.id}` }} canReorder onReorder={(ids) => update({ song_ids: ids })} testid="playlist-track"
                emptyText="Lista vazia. Adicione músicas do repertório."
                rowActions={(s) => <button className="icon-btn" aria-label={`Remover ${s.title} da lista`} onClick={() => update({ song_ids: pl.song_ids.filter((x) => x !== s.id) })} data-testid={`playlist-remove-${s.id}`}><Icon name="close" size={15} /></button>} />
            </div>
            <Button className="auto ghost" icon="add" onClick={() => setAdding((v) => !v)} data-testid="playlist-add-toggle">{adding ? "FECHAR" : "ADICIONAR MÚSICAS"}</Button>
            {adding ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }} data-testid="playlist-catalog">
                {all.filter((s) => !pl.song_ids.includes(s.id)).map((s) => (
                  <SongLine key={s.id} s={s} n="+" actions={<button className="icon-btn" aria-label={`Adicionar ${s.title}`} onClick={() => update({ song_ids: [...pl.song_ids, s.id] })} data-testid={`playlist-add-${s.id}`}><Icon name="add" size={16} /></button>} />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
      {sharing ? <ShareSheet title={`Lista «${pl.name}» · Urban Lisbon Choir`} text={`Partilho consigo a minha lista de estudo «${pl.name}» (${songs.length} músicas).`} url={shareUrl(pl)} onClose={() => setSharing(false)} /> : null}
    </>
  );
}

export function SharedPlaylist() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { create } = usePlaylistMutations();
  const name = sp.get("n") || "Lista partilhada";
  const ids = (sp.get("s") || "").split(",").filter(Boolean);
  // The server returns only the songs this member may see; files are never exposed.
  const q = useQuery({ queryKey: ["shared-playlist", ids.join(",")], queryFn: () => api(`/playlists/shared?ids=${encodeURIComponent(ids.join(","))}`), enabled: ids.length > 0 });
  const songs = q.data?.songs || [];
  const save = () => create.mutate({ name, song_ids: songs.map((s) => s.id) }, { onSuccess: (p) => navigate(`/listas/${p.id}`), onError: (e) => window.alert(e.message) });
  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h1 className="t-h1" style={{ flex: 1, fontSize: 26 }} data-testid="shared-playlist-title">{name}</h1>
        <Button className="auto sm ghost" icon="add" loading={create.isPending} onClick={save} disabled={!songs.length} data-testid="shared-playlist-save">GUARDAR NAS MINHAS LISTAS</Button>
      </div>
      <div className="page ulc-fade" style={{ maxWidth: 820 }}>
        {q.isLoading ? <Spinner /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <TrackList songs={songs} ctx={{ key: `shared:${ids.join(",")}`, label: name, path: `/lista?${sp.toString()}` }} testid="shared-track" emptyText="Esta lista não tem músicas disponíveis." />
            <div className="t-hint">Para alterar a ordem, guarde uma cópia nas suas listas.</div>
            {ids.length > songs.length ? <div className="t-hint" data-testid="shared-playlist-missing">{ids.length - songs.length} música(s) desta lista não estão disponíveis para si.</div> : null}
          </div>
        )}
      </div>
    </>
  );
}
